/**
 * Helpers for tracking which admin-dashboard origin started the current
 * impersonation session.
 *
 * The webapp needs this URL to call `/api/impersonate/stop` and clear the
 * persisted custom claims from the member's Firebase Auth account when the
 * admin clicks "End impersonation". Relying on a build-time
 * `VITE_ADMIN_DASHBOARD_URL` is fragile (and historically optional), so we
 * also accept the value at runtime via the impersonation handoff URL and
 * persist it locally for the lifetime of the impersonated session.
 */

const STORAGE_KEY = "graftus_impersonationAdminUrl";

/**
 * Validate that a candidate string is a safe absolute http(s) URL we can use
 * as the base for `fetch` calls. Returns the normalised origin (no trailing
 * slash) or `null` if the value is missing or unsafe.
 */
export function normalizeAdminUrl(value: string | null | undefined): string | null {
  if (typeof value !== "string" || value.length === 0) return null;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return null;
  }
  return parsed.origin;
}

/**
 * Persist the admin dashboard origin captured from an impersonation handoff
 * URL. No-op if the value is missing or not a safe http(s) URL, or if
 * `localStorage` is unavailable.
 */
export function persistImpersonationAdminUrl(
  rawValue: string | null | undefined,
): void {
  const normalized = normalizeAdminUrl(rawValue);
  if (!normalized) return;
  try {
    localStorage.setItem(STORAGE_KEY, normalized);
  } catch {
    // Ignore quota / privacy mode errors — endImpersonation will fall back
    // to VITE_ADMIN_DASHBOARD_URL if present.
  }
}

/**
 * Read the admin dashboard origin previously captured from an impersonation
 * handoff URL. Falls back to the build-time `VITE_ADMIN_DASHBOARD_URL` env
 * var when nothing is persisted.
 */
export function getImpersonationAdminUrl(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const normalized = normalizeAdminUrl(stored);
    if (normalized) return normalized;
  } catch {
    // localStorage may be unavailable (privacy mode, SSR). Fall through.
  }
  const envUrl = import.meta.env.VITE_ADMIN_DASHBOARD_URL;
  return normalizeAdminUrl(typeof envUrl === "string" ? envUrl : null);
}

/**
 * Clear any persisted admin dashboard origin. Called after the impersonation
 * session ends so a future genuine sign-in by the same browser does not pick
 * up a stale value.
 */
export function clearImpersonationAdminUrl(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}
